import { useCallback, useEffect, useState } from 'react';
import { adresseApi } from '../api/client';
import { piecesApi } from '../api/resources';
import { COTE_MAX, reduitLImage } from '../lib/image';
import { estEnregistre } from '../lib/medias';
import { enAttente, prendEnAttente } from '../lib/pieces-attente';
import { C } from '../theme/palette';

/** Dépose une photo réduite sur un document enregistré. */
async function envoie(genre, documentId, fichier, libelle) {
  const reduit = await reduitLImage(fichier, COTE_MAX);
  await piecesApi.depose(genre, documentId, reduit, libelle || fichier.name);
}

/**
 * Photos jointes à un devis, une facture ou un bon de livraison.
 *
 * Celle d'un bon de livraison est une preuve : c'est ce qui a été déposé sur
 * le chantier, au moment de la remise. Celles d'un devis justifient un prix —
 * un mur à reprendre, une installation à déposer. Elles vivaient encodées dans
 * l'état de l'écran et n'allaient nulle part : les perdre revenait à n'avoir
 * jamais pris la photo.
 *
 * Avec `bouton`, la présentation est celle de la maquette V288 (factures) : les
 * vignettes, puis un bouton pointillé violet pleine largeur. Sur un document pas
 * encore enregistré, les photos restent en brouillon (`brouillons`) et partent
 * au serveur dès qu'il existe (voir `lib/pieces-attente.js`).
 *
 * @param {object} props propriétés du composant
 * @param {'DEVIS'|'FACTURE'|'LIVRAISON'} props.genre genre du document
 * @param {number|string} props.documentId document concerné
 * @param {string} [props.titre] intitulé de la section
 * @param {string} [props.bouton] libellé du bouton pleine largeur (présentation maquette)
 * @param {boolean} [props.note] propose une note avant la photo
 * @param {string} [props.numero] numéro du document, clé des photos en attente
 * @param {object[]} [props.brouillons] photos d'un document pas encore enregistré
 * @param {(pieces: object[]) => void} [props.onBrouillons] met à jour ces photos
 * @param {boolean} [props.plusieurs] plusieurs photos d'un coup, prises ou
 *   choisies dans la photothèque — sinon l'appareil photo s'ouvre d'office
 * @returns {import('react').ReactElement} la galerie et ses commandes
 */
export function PiecesJointes({
  genre,
  documentId,
  titre,
  bouton,
  note,
  numero,
  brouillons,
  onBrouillons,
  plusieurs,
}) {
  const [pieces, setPieces] = useState([]);
  const [envoi, setEnvoi] = useState(false);
  const [erreur, setErreur] = useState('');
  const [libelle, setLibelle] = useState('');
  const enregistre = estEnregistre(documentId);
  const enBrouillon = !enregistre && typeof onBrouillons === 'function';

  const rafraichit = useCallback(async () => {
    if (!enregistre) {
      return;
    }
    try {
      setPieces(await piecesApi.liste(genre, documentId));
    } catch (e) {
      setErreur(e?.message || 'Les pièces n’ont pas pu être lues.');
    }
  }, [genre, documentId, enregistre]);

  // Le document vient d'être créé : les photos prises avant partent maintenant.
  useEffect(() => {
    if (!enregistre) {
      return;
    }
    const attente = numero != null ? prendEnAttente(numero, genre) : [];
    if (attente.length === 0) {
      rafraichit();
      return;
    }
    (async () => {
      try {
        for (const p of attente) {
          await envoie(genre, documentId, p.fichier, p.libelle);
        }
      } catch (e) {
        setErreur(e?.message || 'Une photo n’a pas pu être envoyée.');
      }
      rafraichit();
    })();
  }, [enregistre, genre, documentId, numero, rafraichit]);

  if (!enregistre && !enBrouillon && !bouton) {
    return (
      <div style={{ fontSize: 11, color: C.muted, lineHeight: 1.6 }}>
        Enregistre le document pour y joindre des photos.
      </div>
    );
  }

  const depose = async (evenement) => {
    const fichiers = Array.from((evenement.target && evenement.target.files) || []);
    // Le champ est vide aussitot : sans cela, choisir deux fois le meme
    // fichier ne declencherait rien la seconde fois.
    evenement.target.value = '';
    if (fichiers.length === 0) {
      return;
    }
    setErreur('');
    if (enBrouillon) {
      onBrouillons([
        ...(brouillons || []),
        ...fichiers.map((fichier) => ({
          id: 'b' + Date.now() + Math.random(),
          fichier,
          libelle: libelle.trim(),
          apercu:
            typeof URL !== 'undefined' && URL.createObjectURL ? URL.createObjectURL(fichier) : '',
        })),
      ]);
      setLibelle('');
      return;
    }
    if (!enregistre) {
      return;
    }
    setEnvoi(true);
    try {
      for (const fichier of fichiers) {
        await envoie(genre, documentId, fichier, libelle.trim());
      }
      setLibelle('');
      await rafraichit();
    } catch (e) {
      setErreur(e?.message || 'L’envoi a échoué.');
      await rafraichit();
    } finally {
      setEnvoi(false);
    }
  };

  const retire = async (piece) => {
    if (piece.brouillon) {
      onBrouillons((brouillons || []).filter((x) => x.id !== piece.id));
      return;
    }
    if (!window.confirm('Retirer cette photo ? Le fichier sera effacé.')) {
      return;
    }
    setErreur('');
    try {
      await piecesApi.retire(piece.id);
      await rafraichit();
    } catch (e) {
      setErreur(e?.message || 'Le retrait a échoué.');
    }
  };

  const liste = enregistre
    ? pieces.map((p) => ({
        id: p.id,
        image: (p.typeMedia || '').startsWith('image/'),
        src: adresseApi(p.fichier),
        libelle: p.libelle,
      }))
    : (enBrouillon ? brouillons || [] : enAttente(numero, genre)).map((p) => ({
        id: p.id,
        brouillon: enBrouillon,
        image: !!p.apercu,
        src: p.apercu,
        libelle: p.libelle,
      }));

  const champFichier = (
    <input
      type="file"
      accept="image/*,application/pdf"
      // Pas d'appareil photo imposé : le téléphone propose de prendre une
      // photo, de la choisir, ou de joindre un fichier (un PDF reçu par e-mail).
      multiple={!!plusieurs}
      aria-label="Photo à joindre"
      disabled={envoi}
      style={{ display: 'none' }}
      onChange={depose}
    />
  );

  const vignettes = liste.map((p) => (
    <div key={p.id} style={{ position: 'relative', width: 74 }}>
      {p.image ? (
        <img
          src={p.src}
          alt={p.libelle || 'Pièce jointe'}
          style={{
            width: 74,
            height: 74,
            objectFit: 'cover',
            borderRadius: bouton ? 8 : 9,
            border: '1px solid ' + C.border,
            display: 'block',
          }}
        />
      ) : (
        <a
          href={p.src}
          target="_blank"
          rel="noreferrer"
          style={{
            width: 74,
            height: 74,
            borderRadius: 9,
            border: '1px solid ' + C.border,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 24,
            textDecoration: 'none',
          }}
        >
          📄
        </a>
      )}
      {bouton && p.libelle && (
        <div
          style={{
            fontSize: 7.5,
            color: C.muted,
            marginTop: 2,
            textAlign: 'center',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {p.libelle}
        </div>
      )}
      {(enregistre || p.brouillon) && (
        <button
          type="button"
          aria-label="Retirer la photo"
          onClick={() => retire(p)}
          style={{
            position: 'absolute',
            top: -7,
            right: -7,
            background: C.red,
            color: '#fff',
            border: 'none',
            borderRadius: 99,
            width: 22,
            height: 22,
            fontSize: 13,
            fontWeight: 800,
            cursor: 'pointer',
            lineHeight: 1,
          }}
        >
          ×
        </button>
      )}
    </div>
  ));

  const message = erreur && (
    <div
      role="alert"
      style={{
        marginTop: 6,
        fontSize: bouton ? 9.5 : 11,
        color: C.red,
        background: C.red + '15',
        borderRadius: 7,
        padding: '6px 9px',
        lineHeight: 1.5,
      }}
    >
      {erreur}
    </div>
  );

  if (bouton) {
    return (
      <div>
        {titre && (
          <div
            style={{
              fontSize: 8,
              fontWeight: 800,
              color: C.muted,
              textTransform: 'uppercase',
              letterSpacing: 0.4,
              marginBottom: 6,
            }}
          >
            {titre}
          </div>
        )}
        {note && (
          <input
            aria-label="Note de la photo"
            value={libelle}
            onChange={(e) => setLibelle(e.target.value)}
            placeholder="Note (ex. produit livré, travaux réalisés…)"
            style={{
              width: '100%',
              border: '1px solid ' + C.border,
              borderRadius: 9,
              padding: '10px 11px',
              fontSize: 11,
              marginBottom: 10,
              boxSizing: 'border-box',
            }}
          />
        )}
        {!note && liste.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 8 }}>
            {vignettes}
          </div>
        )}
        <label
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 8,
            border: '1px dashed ' + C.purple,
            borderRadius: 9,
            padding: '14px',
            fontSize: 10.5,
            fontWeight: 700,
            color: C.purple,
            cursor: envoi ? 'wait' : 'pointer',
            width: '100%',
            boxSizing: 'border-box',
          }}
        >
          {envoi ? 'Envoi…' : bouton}
          {champFichier}
        </label>
        {note && liste.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 10 }}>
            {vignettes}
          </div>
        )}
        {message}
      </div>
    );
  }

  return (
    <div>
      {titre && (
        <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, marginBottom: 5 }}>
          {titre}
        </div>
      )}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7 }}>
        {vignettes}
        <label
          style={{
            width: 74,
            height: 74,
            border: '1px dashed ' + C.teal,
            borderRadius: 9,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 11,
            fontWeight: 700,
            color: C.teal,
            cursor: envoi ? 'wait' : 'pointer',
            opacity: envoi ? 0.6 : 1,
            textAlign: 'center',
          }}
        >
          <span style={{ fontSize: 18 }}>📷</span>
          {envoi ? 'Envoi…' : 'Ajouter'}
          {champFichier}
        </label>
      </div>
      {message}
    </div>
  );
}
